/*
 * Product card colour swatches: one row, scrolled with arrows.
 *
 * The client wants every colour reachable from the category page without
 * opening the product: the row stays one line, and when a product has more
 * colours than fit, the last slot is a → arrow and each click on it brings
 * in the next colour. Once scrolled, a ← takes the first slot and steps
 * back. Each arrow shows only while there is more in its direction.
 * Clicking a colour still swaps the card image (Ella's color-swatch), so the
 * shopper can see every colour's photo from the grid.
 *
 * This replaced a "+N" link to the product page.
 *
 * How many circles fit depends on the card's width (3 across on desktop, 1 on
 * a phone, narrower on small laptops), so it is measured, not fixed: once on
 * load, again whenever a card changes width, and on cards added later by
 * filtering, sorting or paging, which replace the grid without a page load.
 */
(() => {
  if (window.__swatchOneRow) return;
  window.__swatchOneRow = true;

  const LIST = ".card__swatch .swatch-list";
  const state = new WeakMap(); // list -> { start, end, width }

  const CHEVRON = (direction) =>
    `<svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true" focusable="false">` +
    `<path d="${direction === "prev" ? "M12.5 4 6.5 10l6 6" : "M7.5 4l6 6-6 6"}" ` +
    `fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

  function arrow(list, direction) {
    let li = list.querySelector(`:scope > .swatch-arrow--${direction}`);
    if (li) return li;
    li = document.createElement("li");
    li.className = `swatch-arrow swatch-arrow--${direction}`;
    li.hidden = true;
    li.innerHTML =
      `<button type="button" class="swatch-arrow__button" ` +
      `aria-label="${direction === "prev" ? "Previous colours" : "More colours"}">${CHEVRON(direction)}</button>`;
    li.querySelector("button").addEventListener("click", (event) => {
      // Not a colour: keep Ella's swatch handler and the card link out of it.
      event.preventDefault();
      event.stopPropagation();
      const s = state.get(list) || {};
      if (direction === "next") render(list, { end: (s.end || 0) + 1 });
      else render(list, { start: (s.start || 0) - 1 });
    });
    if (direction === "prev") list.prepend(li);
    else list.append(li);
    return li;
  }

  /** How many 37px slots fit across the list: at least ←, a colour and →. */
  function capacity(list, items) {
    const sample = items.find((item) => item.offsetWidth > 0) || items[0];
    const slot = sample.getBoundingClientRect().width;
    const gap = parseFloat(getComputedStyle(list).columnGap) || 0;
    const width = list.getBoundingClientRect().width;
    if (!slot || !width) return items.length;
    return Math.max(3, Math.floor((width + gap) / (slot + gap)));
  }

  /**
   * Which colours show, given how many slots fit. Anchored on the first
   * colour (← and refits) or the last (→), so each → click always brings
   * in one new colour, even the first, when the ← arrives and takes a slot.
   */
  function windowFor(total, fit, anchor) {
    if ("end" in anchor) {
      const end = Math.min(total, Math.max(1, anchor.end));
      const hasNext = end < total;
      let room = fit - (hasNext ? 1 : 0);
      if (end - room > 0) room -= 1; // the ← takes a slot
      if (end - room > 0) return { start: end - room, end, hasPrev: true, hasNext };
      anchor = { start: 0 };
    }
    let start = Math.max(0, Math.min(anchor.start, total - 1));
    const hasPrev = start > 0;
    let room = fit - (hasPrev ? 1 : 0);
    const hasNext = start + room < total;
    if (hasNext) room -= 1;
    // At the end, pull the window back so the row stays full. Only reached
    // with a ← showing: from the first colour there is always a →, since
    // windowFor is only asked when there are more colours than slots.
    else start = total - room;
    return { start, end: Math.min(total, start + room), hasPrev, hasNext };
  }

  function render(list, anchor) {
    const items = [...list.querySelectorAll(":scope > .swatch-item")];
    if (items.length === 0) return;
    const prev = arrow(list, "prev");
    const next = arrow(list, "next");

    // Measure with every colour showing and no arrows.
    items.forEach((item) => item.classList.remove("is-overflow"));
    prev.hidden = true;
    next.hidden = true;
    list.classList.add("is-fitted");

    const s = state.get(list) || {};
    const total = items.length;
    const fit = capacity(list, items);
    const view =
      total <= fit
        ? { start: 0, end: total, hasPrev: false, hasNext: false }
        : windowFor(total, fit, anchor || { start: s.start || 0 });
    state.set(list, { ...s, start: view.start, end: view.end });

    items.forEach((item, index) => {
      item.classList.toggle("is-overflow", index < view.start || index >= view.end);
    });
    prev.hidden = !view.hasPrev;
    next.hidden = !view.hasNext;
  }

  const resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      const width = Math.round(entry.contentRect.width);
      const s = state.get(entry.target) || {};
      // Refit only when the width changed: showing and hiding circles
      // changes the height too, which this observer also reports.
      if (width === 0 || s.width === width) continue;
      state.set(entry.target, { ...s, width });
      render(entry.target);
    }
  });

  function watch(root) {
    root.querySelectorAll(LIST).forEach((list) => {
      if (list.dataset.oneRow) return;
      list.dataset.oneRow = "true";
      resizeObserver.observe(list);
    });
  }

  let queued = false;
  new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      watch(document);
    });
  }).observe(document.documentElement, { childList: true, subtree: true });

  watch(document);
})();
